// Datos del contrato que se leen antes de firmar: el acreditado y el equipo con su IMEI. El IMEI se
// captura y se fija aquí con /pieces, porque va impreso en el contrato.
import { useState, type ReactNode } from 'react';
import { Lock } from 'lucide-react';
import { Button } from '@/kit';
import { usePinPieces } from '../api/useWizard';
import {
    IMEI_LENGTH,
    financedImeiPinBody,
    financedPhoneName,
    financedPhoneOf,
    pinnedFinancedImei,
} from '../deliveryPieces';
import { pinPiecesFailure } from '../piecesErrors';
import { IdentifierField } from './delivery/IdentifierField';
import type { WizardSession } from '../types';

const HINT = 'text-[11px] leading-[1.35] text-muted-foreground';

function Row({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="flex items-center justify-between gap-3 text-[12.5px]">
            <span className="flex-none text-muted-foreground">{label}</span>
            <span className="min-w-0 text-right font-medium">{children}</span>
        </div>
    );
}

export interface ContractDeviceBlockProps {
    sessionId: string;
    session: WizardSession;
    contractData: Partial<Record<string, string>>;
    /** El código ya salió o el contrato ya se firmó: el IMEI no se cambia. */
    locked: boolean;
    /** El vendedor reabrió el campo para cambiar el IMEI guardado. */
    editing: boolean;
    onEditingChange: (editing: boolean) => void;
    /** El IMEI quedó fijado: el contrato hay que releerlo. */
    onPinned: () => void;
    /** El silo pidió releer la venta. */
    onReload: () => void;
}

export function ContractDeviceBlock({
    sessionId,
    session,
    contractData,
    locked,
    editing,
    onEditingChange,
    onPinned,
    onReload,
}: ContractDeviceBlockProps) {
    const pinPieces = usePinPieces(sessionId);
    const financed = financedPhoneOf(session);
    const pinned = pinnedFinancedImei(session);
    const phoneName = financedPhoneName(session);
    const [draft, setDraft] = useState('');
    const [error, setError] = useState<string | undefined>();
    const capturing = !locked && (pinned === '' || editing);

    const save = (): void => {
        const body = financedImeiPinBody(session, draft);
        if (!body) return;
        setError(undefined);
        pinPieces.mutate(body, {
            onSuccess: () => {
                onEditingChange(false);
                onPinned();
            },
            onError: (e) => {
                const failure = pinPiecesFailure(e);
                setError(failure.message);
                if (failure.action === 'reload') onReload();
            },
        });
    };

    return (
        <div
            data-testid="sign-contract-device"
            className="mt-3 flex flex-col gap-2 border-t border-border pt-3"
        >
            {contractData.nombreAcreditado !== undefined && (
                <Row label="Nombre del acreditado">{contractData.nombreAcreditado}</Row>
            )}
            {contractData.curp !== undefined && (
                <Row label="CURP">
                    <span className="font-mono">{contractData.curp}</span>
                </Row>
            )}
            {capturing ? (
                <div className="flex flex-col gap-2 rounded-lg border border-dashed border-border bg-muted/40 px-3 py-2.5">
                    <IdentifierField
                        sessionId={sessionId}
                        lineId={financed?.lineId}
                        sku={financed?.sku}
                        mode="IMEI"
                        label={phoneName}
                        value={draft}
                        onChange={(value) => {
                            setDraft(value);
                            setError(undefined);
                        }}
                        error={error}
                        disabled={pinPieces.isPending}
                    />
                    {!error && draft.length < IMEI_LENGTH && (
                        <p
                            className={HINT}
                        >{`Faltan ${String(IMEI_LENGTH - draft.length)} dígitos.`}</p>
                    )}
                    <div className="flex flex-wrap items-center justify-end gap-2">
                        <div className="flex gap-2">
                            {editing && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    disabled={pinPieces.isPending}
                                    onClick={() => {
                                        setError(undefined);
                                        onEditingChange(false);
                                    }}
                                >
                                    Cancelar
                                </Button>
                            )}
                            <Button
                                size="sm"
                                disabled={draft.length !== IMEI_LENGTH}
                                isLoading={pinPieces.isPending}
                                onClick={save}
                            >
                                Guardar IMEI
                            </Button>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <Row label="Equipo">{phoneName}</Row>
                    {pinned !== '' && (
                        <Row label="IMEI">
                            <span className="flex items-center justify-end gap-2">
                                <span
                                    data-testid="sign-pinned-imei"
                                    className="font-mono tracking-[0.06em] tabular-nums"
                                >
                                    {pinned}
                                </span>
                                {locked ? (
                                    <Lock
                                        className="size-3 text-muted-foreground"
                                        aria-label="Va en el contrato"
                                    />
                                ) : (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                            setDraft(pinned);
                                            setError(undefined);
                                            onEditingChange(true);
                                        }}
                                    >
                                        Cambiar
                                    </Button>
                                )}
                            </span>
                        </Row>
                    )}
                </>
            )}
        </div>
    );
}
